// GTM Utility functions for consistent tracking across the application

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

export interface GTMEventData {
  event: string;
  page_title?: string;
  page_location?: string;
  page_path?: string;
  [key: string]: any;
}

/**
 * Push an event to GTM dataLayer
 * @param eventData - The event data to push
 */
export const pushGTMEvent = (eventData: GTMEventData): void => {
  if (window.dataLayer) {
    window.dataLayer.push(eventData);
  }
};

/**
 * Track a page view event
 * @param pageTitle - The title of the page
 * @param pagePath - The path of the page (optional, defaults to current path)
 */
export const trackPageView = (pageTitle: string, pagePath?: string): void => {
  pushGTMEvent({
    event: 'page_view',
    page_title: pageTitle,
    page_location: window.location.href,
    page_path: pagePath || window.location.pathname
  });
};

/**
 * Track a custom event
 * @param eventName - The name of the event
 * @param eventData - Additional event data
 */
export const trackCustomEvent = (eventName: string, eventData: Record<string, any> = {}): void => {
  pushGTMEvent({
    event: eventName,
    timestamp: new Date().toISOString(),
    ...eventData
  });
};

/**
 * Track user journey events (like signup to loading transition)
 * @param journeyStep - The step in the user journey
 * @param userData - User-related data
 */
export const trackUserJourney = (journeyStep: string, userData: Record<string, any> = {}): void => {
  pushGTMEvent({
    event: 'user_journey_step',
    journey_step: journeyStep,
    timestamp: new Date().toISOString(),
    ...userData
  });
};

/**
 * Initialize GTM tracking for a component
 * @param componentName - The name of the component
 * @param additionalData - Additional data to track
 */
export const initializeGTMTracking = (componentName: string, additionalData: Record<string, any> = {}): void => {
  trackPageView(componentName);
  trackCustomEvent(`${componentName}_viewed`, additionalData);
};

/**
 * Hook to track route changes for React Router
 * This should be used in the main App component or router wrapper
 */
export const useGTMRouteTracking = () => {
  const trackRouteChange = (pathname: string, search: string = '') => {
    const fullPath = pathname + search;
    const pageTitle = getPageTitleFromPath(pathname);
    
    trackPageView(pageTitle, fullPath);
    
    // Track specific route events
    if (fullPath.includes('/loading-dashboard')) {
      trackCustomEvent('loading_dashboard_accessed', {
        path: fullPath,
        referrer: document.referrer
      });
    }
  };

  return { trackRouteChange };
};

/**
 * Track a real purchase event for GTM
 * @param purchaseData - The purchase data to track
 */
export const trackPurchaseEvent = (purchaseData: {
  price: number;
  currency: string;
  coupon?: string;
  customer_type: 'with_cc' | 'without_cc';
  items: {
    item_name: string;
  }[];
}): void => {
  // Push to GTM dataLayer
  pushGTMEvent({
    event: 'purchase',
    value: purchaseData.customer_type === 'without_cc' ? 0 : purchaseData.price,
    currency: purchaseData.currency,
    coupon: purchaseData.coupon || '',
    customer_type: purchaseData.customer_type,
    items: [
      {
       item_name: purchaseData.items[0].item_name
      }
    ]
  });

  // Also push to gtag if available (for Google Analytics/Ads)
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'purchase', {
      value: purchaseData.customer_type === 'without_cc' ? 0 : purchaseData.price,
      currency: purchaseData.currency,
      coupon: purchaseData.coupon || '',
      items: purchaseData.items,
      customer_type: purchaseData.customer_type,
    });
  }
};
/**
 * Get page title from path
 * @param pathname - The current pathname
 * @returns The page title
 */
const getPageTitleFromPath = (pathname: string): string => {
  const pathMap: Record<string, string> = {
    '/': 'Home',
    '/login': 'Login',
    '/signup': 'Signup',
    '/loading-dashboard': 'Loading Dashboard',
    '/dashboard': 'Dashboard',
    '/forgot-password': 'Forgot Password',
    '/verify-code': 'Verify Code',
    '/sso-signup': 'SSO Signup',
    '/super-admin': 'Super Admin'
  };

  // Find the best match for the pathname
  for (const [path, title] of Object.entries(pathMap)) {
    if (pathname.startsWith(path)) {
      return title;
    }
  }

  return 'Page';
}; 